/* ============================================================
   Community Picks — Shopify Forms visual enhancer.

   The Shopify Forms app renders inside an OPEN shadow root, so colours /
   radius / padding are handled by CSS custom properties (see the .css file).
   The few things those variables can't do — chamfered corners, inline field
   icons and the button arrow — are added here by reaching into the open
   shadowRoot and injecting a scoped <style> + a couple of decorative nodes.

   IMPORTANT — re-render safety:
   The app is React-based and re-renders the form on submit / validation, which
   resets the className on its own elements. So NONE of our styling depends on a
   class we add — every rule keys off the app's own stable class prefixes
   (_formFieldContainer_, _formInputFieldLabel_). We only inject the icon / arrow
   NODES, and a MutationObserver re-adds those if a re-render drops them.

   Defensive by design:
   - Only touches forms inside .ccp-form-slot.
   - Idempotent: never duplicates icons / styles.
   - Self-healing: a MutationObserver re-applies if the app re-renders.
   - Accessible: icons are aria-hidden; focus rings are re-added as INSET
     shadows so they survive clip-path; the button keeps its real label.
   - Graceful: if a Shopify Forms update changes the data-testid / class hooks,
     the form still works — it just falls back to the CSS-variable styling.
   ============================================================ */
(function () {
  var CHAMFER =
    'polygon(7px 0,calc(100% - 7px) 0,100% 7px,100% calc(100% - 7px),' +
    'calc(100% - 7px) 100%,7px 100%,0 calc(100% - 7px),0 7px)';

  var ICONS = {
    email:
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1.5"/><path d="m3 6.5 9 6 9-6"/></svg>',
    sparkle:
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.1 6.4L20.5 10.5 14.1 12.6 12 19l-2.1-6.4L3.5 10.5 9.9 8.4z"/></svg>',
    pencil:
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>',
    arrow:
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>'
  };

  /* All field/button rules key off the APP's own stable class prefixes so they
     survive React re-renders. !important beats the app's adopted stylesheet.
     Rules for our own nodes (.ccp-fi / .ccp-btn-arrow) don't need it. */
  var FC = 'form[data-testid="form"] [class*="_formFieldContainer_"]';
  var STYLE = [
    FC + '{position:relative !important;margin-bottom:14px !important;}',
    FC + ' input,' + FC + ' textarea{clip-path:' + CHAMFER + ' !important;padding-left:46px !important;}',
    FC + ':has(textarea){clip-path:' + CHAMFER + ' !important;}',
    FC + ' input:focus,' + FC + ' textarea:focus{box-shadow:inset 0 0 0 2px #ff3b30 !important;}',
    FC + ':has(textarea):focus-within::before{box-sizing:border-box !important;border-top:2.4px solid #ff3b30 !important;border-left:2.4px solid #ff3b30 !important;border-right:2.4px solid #ff3b30 !important;right:0 !important;}',
    FC + ' [class*="_formInputFieldLabel_"],' + FC + ' label{left:46px !important;}',
    '.ccp-fi{position:absolute;left:18px;top:0;bottom:0;display:flex;align-items:center;color:#8a8a90;pointer-events:none;z-index:3;}',
    '.ccp-fi--area{bottom:auto;top:18px;}',
    'button[data-testid="btn-form-submit"]{clip-path:' + CHAMFER + ' !important;text-transform:uppercase !important;letter-spacing:.04em !important;display:inline-flex !important;align-items:center !important;justify-content:center !important;gap:8px !important;width:auto !important;min-width:0 !important;align-self:flex-start !important;margin-top:4px !important;margin-inline-end:auto !important;padding-left:32px !important;padding-right:32px !important;}',
    'button[data-testid="btn-form-submit"]:focus-visible{box-shadow:inset 0 0 0 2px #fff !important;}',
    '.ccp-btn-arrow{display:inline-flex;align-items:center;}'
  ].join('');

  function ensureStyle(root) {
    if (root.getElementById('ccp-form-style')) return;
    var s = document.createElement('style');
    s.id = 'ccp-form-style';
    s.textContent = STYLE;
    root.appendChild(s);
  }

  function iconFor(field) {
    if (field.tagName === 'TEXTAREA') return 'pencil';
    var t = (field.getAttribute('data-testid') || field.id || field.name || '').toLowerCase();
    if (t.indexOf('email') !== -1) return 'email';
    if (t.indexOf('character') !== -1 || t.indexOf('name') !== -1) return 'sparkle';
    return 'sparkle';
  }

  function decorate(root) {
    ensureStyle(root);
    var form = root.querySelector('form[data-testid="form"]');
    if (!form) return; // success / non-form step — nothing to decorate

    var containers = form.querySelectorAll('[class*="_formFieldContainer_"]');
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (c.querySelector('.ccp-fi')) continue; // already decorated
      var field = c.querySelector('input, textarea, select');
      if (!field) continue;
      var isArea = field.tagName === 'TEXTAREA';
      var span = document.createElement('span');
      span.className = 'ccp-fi' + (isArea ? ' ccp-fi--area' : '');
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = ICONS[iconFor(field)];
      // Append (not insertBefore): trailing nodes survive React reconciliation
      // better, and the icon is absolutely positioned so DOM order is irrelevant.
      c.appendChild(span);
    }

    var btn = form.querySelector('button[data-testid="btn-form-submit"]');
    if (btn && !btn.querySelector('.ccp-btn-arrow')) {
      var a = document.createElement('span');
      a.className = 'ccp-btn-arrow';
      a.setAttribute('aria-hidden', 'true');
      a.innerHTML = ICONS.arrow;
      btn.appendChild(a);
    }
  }

  function hook(host) {
    var root = host.shadowRoot;
    if (!root) return false;
    decorate(root);
    if (!host.__ccpObserved) {
      host.__ccpObserved = true;
      var mo = new MutationObserver(function () {
        if (host.__ccpRaf) return;
        host.__ccpRaf = requestAnimationFrame(function () {
          host.__ccpRaf = null;
          decorate(root);
        });
      });
      mo.observe(root, { childList: true, subtree: true });
    }
    return true;
  }

  function init() {
    var hosts = document.querySelectorAll('.ccp-form-slot shopify-forms-embed');
    if (!hosts.length) return false;
    var all = true;
    hosts.forEach(function (h) {
      if (!hook(h)) all = false;
    });
    return all;
  }

  var tries = 0;
  function tick() {
    if (init() || tries++ > 40) return; // retry ~10s while the app hydrates
    setTimeout(tick, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick);
  } else {
    tick();
  }
})();
